import {useEffect, useMemo, useState} from 'react';
import type {TaskAssignmentResponse} from 'src/api/ProactApiClient';
import {useTaskAssignmentTransition} from 'src/app/tasks/offline/useTaskAssignmentTransition';
import {
  isFormSubmissionProcessing,
  PENDING_SYNC_PILL,
  PROCESSING_PILL,
  PROCESSING_POLL_MS,
  TaskAssignmentPillConfig,
} from 'src/app/tasks/taskHelpers';
import type {PillProps} from 'src/design/atoms/Pill';
import {isPendingSync} from 'src/offline/pendingSync';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useGetTaskAssignmentDetailsQuery} from 'src/redux/queries/taskAssignment';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {ApiEndpoints} from 'src/shared/utils/ApiCaller';
import type {IStateMachine} from 'src/types/StateMachine';

export interface UseTaskAssignmentViewResult {
  viewItem: TaskAssignmentResponse | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  machine: IStateMachine | undefined;
  pills: PillProps[];
  postTransition: ReturnType<typeof useTaskAssignmentTransition>;
}

/** Read model for the user-facing task-assignment view (full page and dashboard preview). */
export const useTaskAssignmentView = (
  assignmentId: number | undefined
): UseTaskAssignmentViewResult => {
  const [machine] = useStateMachine({
    url: ApiEndpoints.getTaskAssignmentStateMachine,
  });
  const postTransition = useTaskAssignmentTransition(assignmentId);
  const {isOffline} = useConnectivity();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    data: viewItem,
    isLoading,
    error,
    refetch,
  } = useGetTaskAssignmentDetailsQuery(assignmentId as number, {
    skip: assignmentId === undefined,
    pollingInterval: isProcessing && !isOffline ? PROCESSING_POLL_MS : 0,
  });

  useEffect(() => {
    setIsProcessing(!!viewItem && isFormSubmissionProcessing(viewItem));
  }, [viewItem]);

  const pills = useMemo<PillProps[]>(
    () =>
      viewItem
        ? [
            ...(isPendingSync(viewItem) ? [PENDING_SYNC_PILL] : []),
            ...(isFormSubmissionProcessing(viewItem) ? [PROCESSING_PILL] : []),
            ...TaskAssignmentPillConfig(
              viewItem.state,
              viewItem.taskDefinition.taskType,
              viewItem.taskDefinition.state,
              viewItem.taskDefinition.dueAt
            ),
          ]
        : [],
    [viewItem]
  );

  return {
    viewItem,
    isLoading,
    error,
    refetch: () => void refetch(),
    machine,
    pills,
    postTransition,
  };
};
